/**
 * Сервис проверки наличия объекта в Supabase Storage.
 * @module storageService
 *
 * @description
 * Нужен как страховка перед созданием записи: TUS может «мгновенно завершить»
 * загрузку по унаследованному ключу, объект под которым уже удалён. Без этой
 * проверки запись уходит в обработку и падает через ~40 секунд на скачивании.
 *
 * Проверка намеренно трёхзначная. `"missing"` блокирует создание записи, поэтому
 * он допускается только как результат двух успешных ответов Storage. Любая
 * неполная проверка — таймаут, ошибка, исключение — это `"unknown"`, на котором
 * загрузка продолжается по-старому (fail-open).
 */

import { supabase } from "@/integrations/supabase/client";

export type ObjectExistenceStatus = "exists" | "missing" | "unknown";

interface CheckObjectExistsOptions {
    /** Внешняя отмена: прекращает попытку, паузу и повторы */
    signal?: AbortSignal;
}

/** Итог одной попытки: промах засчитывается только при успешном ответе */
type AttemptOutcome = "exists" | "miss" | "failed";

const MAX_ATTEMPTS = 2;
const ATTEMPT_TIMEOUT_MS = 5_000;
const RETRY_DELAY_MS = 300;

const abortError = (signal: AbortSignal): unknown =>
    signal.reason ?? new DOMException("Проверка объекта отменена", "AbortError");

const throwIfAborted = (signal?: AbortSignal) => {
    if (signal?.aborted) {
        throw abortError(signal);
    }
};

/** Пауза, прерываемая внешней отменой */
const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError(signal));
            return;
        }

        const cleanup = () => {
            clearTimeout(timer);
            signal?.removeEventListener("abort", onAbort);
        };

        const onAbort = () => {
            cleanup();
            reject(abortError(signal!));
        };

        const timer = setTimeout(() => {
            cleanup();
            resolve();
        }, ms);

        signal?.addEventListener("abort", onAbort, { once: true });
    });

/**
 * Одна попытка запроса к Storage.
 *
 * У каждой попытки собственный `AbortController`: он гасится таймаутом и
 * связывается с внешним сигналом вручную. `AbortSignal.any` не используется —
 * его нет в части поддерживаемых браузеров.
 */
const runListAttempt = async (
    bucket: string,
    key: string,
    externalSignal?: AbortSignal
): Promise<AttemptOutcome> => {
    const controller = new AbortController();
    const forwardAbort = () => controller.abort(abortError(externalSignal!));
    const timeoutId = setTimeout(
        () => controller.abort(new DOMException("Таймаут проверки объекта", "TimeoutError")),
        ATTEMPT_TIMEOUT_MS
    );

    externalSignal?.addEventListener("abort", forwardAbort, { once: true });

    try {
        const { data, error } = await supabase.storage
            .from(bucket)
            .list("", { search: key }, { signal: controller.signal });

        throwIfAborted(externalSignal);

        if (error) {
            console.error("storageService.checkObjectExists: Supabase error", error);
            return "failed";
        }

        if (!Array.isArray(data)) {
            console.error("storageService.checkObjectExists: unexpected response shape");
            return "failed";
        }

        // `search` в Storage работает по подстроке, поэтому сравнение строгое
        return data.some((item) => item?.name === key) ? "exists" : "miss";
    } catch (error) {
        throwIfAborted(externalSignal);

        console.error("storageService.checkObjectExists: request failed", error);
        return "failed";
    } finally {
        clearTimeout(timeoutId);
        externalSignal?.removeEventListener("abort", forwardAbort);
    }
};

/**
 * Проверяет, лежит ли объект `key` в бакете `bucket`.
 *
 * @returns `"exists"` — объект найден; `"missing"` — два успешных ответа без него;
 *          `"unknown"` — проверку не удалось довести до конца
 * @throws Причину отмены, если сработал внешний `AbortSignal`
 */
export async function checkObjectExists(
    bucket: string,
    key: string,
    options: CheckObjectExistsOptions = {}
): Promise<ObjectExistenceStatus> {
    const { signal } = options;
    let successfulMisses = 0;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        throwIfAborted(signal);

        if (attempt > 0) {
            await delay(RETRY_DELAY_MS, signal);
        }

        const outcome = await runListAttempt(bucket, key, signal);

        if (outcome === "exists") {
            return "exists";
        }

        if (outcome === "miss") {
            successfulMisses += 1;
        }
    }

    // Только полностью успешная проверка даёт право заблокировать создание записи
    return successfulMisses === MAX_ATTEMPTS ? "missing" : "unknown";
}
